import { el, clear, fmtInt, relativeTime } from '../ui/dom';
import { renderShell, renderError, renderLoading } from '../ui/shell';
import { listOrganizations, refreshTeamsBilling } from '../api/monitoring';
import { ApiError, UnauthorizedError } from '../api/client';
import { navigate } from '../router';
import type { RouteContext } from '../router';
import type { OrganizationSummary } from '../api/types';
import {
  CANCELLING_FILTER_UNSUPPORTED,
  cancellingFilterIgnored,
  orgsListUrl,
  parseOrgsListState,
  toggleCancelling,
} from './orgs-list-support';
import type { OrgsSortBy } from './orgs-list-support';
import {
  REFRESH_BUTTON_LABEL,
  refreshFailureMessage,
  refreshSummary,
  renewalLabel,
  tonePillClass,
} from './teams-billing-format';

// Mirrors PAID_TIERS in api/src/auth-v2/entitlement.service.ts: the tiers
// that actually carry a paid Teams entitlement.
const PAID_TIERS = new Set(['BUSINESS', 'ENTERPRISE']);

export async function renderOrgsList({ root, query }: RouteContext) {
  const content = renderShell(root, 'teams');

  // State seeded from the URL so views are shareable / reload-safe.
  const state = parseOrgsListState(query);

  const searchInput = el('input', {
    class: 'control control-search',
    type: 'search',
    placeholder: 'Search teams by name or slug…',
    value: state.search,
  });

  const windowSelect = selectControl(
    [
      ['7', 'Last 7 days'],
      ['30', 'Last 30 days'],
      ['90', 'Last 90 days'],
    ],
    String(state.windowDays),
  );

  const sortSelect = selectControl(
    [
      ['createdAt:desc', 'Newest'],
      ['createdAt:asc', 'Oldest'],
      ['name:asc', 'Name A–Z'],
      ['subscriptionTier:asc', 'Tier'],
    ],
    `${state.sortBy}:${state.order}`,
  );

  // One toggle: on, the list keeps only teams whose subscription is cancelled
  // but still active (the API's renewal=cancelling).
  const cancellingChip = el(
    'button',
    {
      class: 'seg',
      type: 'button',
      onClick: () => {
        Object.assign(state, toggleCancelling(state));
        syncChip();
        void load();
      },
    },
    ['Cancelling'],
  );
  function syncChip() {
    cancellingChip.className = state.renewal ? 'seg seg-on' : 'seg';
    cancellingChip.setAttribute('aria-pressed', String(Boolean(state.renewal)));
  }
  syncChip();

  const controls = el('div', { class: 'controls' }, [
    searchInput,
    el('div', { class: 'controls-right' }, [
      el('div', { class: 'segmented' }, [cancellingChip]),
      windowSelect,
      sortSelect,
    ]),
  ]);

  const refreshButton = el(
    'button',
    { class: 'btn btn-ghost', type: 'button', onClick: () => void refreshBilling() },
    [REFRESH_BUTTON_LABEL],
  );
  const refreshStatus = el('span', { class: 'refresh-status', role: 'status' });
  const head = el('div', { class: 'view-head', style: 'margin-bottom:16px' }, [
    el('div', { class: 'page-head' }, [el('h1', {}, ['Teams'])]),
    el('div', { class: 'refresh-controls' }, [refreshStatus, refreshButton]),
  ]);

  const results = el('div', { class: 'results' });
  content.append(head, controls, results);

  function setRefreshStatus(text: string, tone: 'ok' | 'error') {
    refreshStatus.textContent = text;
    refreshStatus.className =
      tone === 'error' ? 'refresh-status refresh-status-error' : 'refresh-status';
  }

  // Re-reads every Teams subscription from Apple and Stripe, then reloads the
  // list so the Renewal column shows what that found. The message stays put
  // across the reload: it lives outside `results`.
  async function refreshBilling() {
    refreshButton.disabled = true;
    refreshButton.textContent = 'Refreshing…';
    setRefreshStatus('', 'ok');
    try {
      const result = await refreshTeamsBilling();
      setRefreshStatus(refreshSummary(result), 'ok');
      await load();
    } catch (err) {
      if (err instanceof UnauthorizedError) return navigate('/login');
      setRefreshStatus(
        refreshFailureMessage(
          err instanceof ApiError ? err.status : undefined,
          err instanceof Error ? err.message : '',
        ),
        'error',
      );
    } finally {
      refreshButton.disabled = false;
      refreshButton.textContent = REFRESH_BUTTON_LABEL;
    }
  }

  function syncUrl() {
    history.replaceState({}, '', orgsListUrl(location.pathname, state));
  }

  // Responses can finish out of order (a double-click on the chip, a refresh's
  // reload overlapping a filter change): only the newest request paints, so the
  // table never shows rows for filters that are no longer on.
  let loadSeq = 0;

  async function load() {
    const seq = ++loadSeq;
    renderLoading(results, 'Loading teams…');
    syncUrl();
    try {
      const data = await listOrganizations({
        search: state.search || undefined,
        sortBy: state.sortBy,
        order: state.order,
        windowDays: state.windowDays,
        page: state.page,
        renewal: state.renewal,
      });
      if (seq !== loadSeq) return;
      if (cancellingFilterIgnored(state.renewal, data.items)) {
        renderError(results, CANCELLING_FILTER_UNSUPPORTED);
        return;
      }
      renderTable(results, data.items, state.windowDays);
      renderPagination(results, data, (nextPage) => {
        state.page = nextPage;
        void load();
      });
    } catch (err) {
      if (seq !== loadSeq) return;
      if (err instanceof UnauthorizedError) return navigate('/login');
      renderError(results, err instanceof Error ? err.message : 'Failed to load teams.');
    }
  }

  let debounce: number | undefined;
  searchInput.addEventListener('input', () => {
    window.clearTimeout(debounce);
    debounce = window.setTimeout(() => {
      state.search = searchInput.value.trim();
      state.page = 1;
      void load();
    }, 350);
  });

  windowSelect.addEventListener('change', () => {
    state.windowDays = Number(windowSelect.value);
    state.page = 1;
    void load();
  });

  sortSelect.addEventListener('change', () => {
    const [by, ord] = sortSelect.value.split(':');
    state.sortBy = by as OrgsSortBy;
    state.order = ord as 'asc' | 'desc';
    state.page = 1;
    void load();
  });

  await load();
}

function selectControl(
  options: [string, string][],
  selected: string,
): HTMLSelectElement {
  const sel = el('select', { class: 'control' });
  for (const [value, label] of options) {
    const opt = el('option', { value, textContent: label });
    if (value === selected) opt.selected = true;
    sel.append(opt);
  }
  return sel;
}

function renderTable(
  container: HTMLElement,
  items: OrganizationSummary[],
  windowDays: number,
): void {
  clear(container);
  if (items.length === 0) {
    container.append(el('div', { class: 'empty' }, ['No teams match.']));
    return;
  }

  const headers = [
    'Team',
    'Tier',
    'Status',
    'Renewal',
    'Members',
    'Devices',
    'Venues',
    `Scans (${windowDays}d)`,
    'Passback',
    'Rejected',
    'Last scan',
  ];

const thead = el('thead', {}, [
    el('tr', {}, headers.map((h) => el('th', { textContent: h }))),
  ]);

  const rows = items.map((o) => {
    const passbackCls = o.metrics.passbackHits > 0 ? 'cell-flag' : '';
    // Green means ENTITLED, not merely "status says ACTIVE".
    //
    // subscriptionTier and subscriptionStatus are independent columns, so a
    // FREE org sitting at status ACTIVE is a perfectly normal, unentitled org
    // — and this used to paint it the same green as a paying customer. That
    // read as "this org is fine and paying" while its scans were being refused
    // for lack of a subscription, which is exactly backwards for a monitoring
    // tool. Mirrors EntitlementService.evaluate on the API
    // (api/src/auth-v2/entitlement.service.ts): a paid tier AND ACTIVE AND not
    // suspended. If that predicate changes, change this with it.
    const entitled =
      PAID_TIERS.has(o.subscriptionTier) &&
      o.subscriptionStatus === 'ACTIVE' &&
      !o.suspendedAt;
    const statusCls = o.suspendedAt
      ? 'pill pill-danger'
      : entitled
        ? 'pill pill-ok'
        : 'pill pill-warn';
    // Null: no renewing or cancelling paid subscription, or an API that
    // predates the field. Either way there is nothing to say, so a dash.
    const renewal = renewalLabel(o.renewal);
    return el(
      'tr',
      {
        class: 'row-clickable',
        onClick: () => navigate(`/organizations/${o.id}`),
      },
      [
        el('td', {}, [
          el('div', { class: 'cell-strong', textContent: o.name }),
          el('div', { class: 'cell-muted', textContent: o.slug }),
        ]),
        el('td', {}, [el('span', { class: 'pill', textContent: o.subscriptionTier })]),
        el('td', {}, [
          el('span', {
            class: statusCls,
            // The raw column value still shows — an admin tool must not hide
            // what the database actually says — but an unentitled org gets the
            // conclusion spelled out, because "FREE / ACTIVE" is the exact pair
            // that reads as "paying" at a glance.
            textContent: o.suspendedAt ? 'SUSPENDED' : o.subscriptionStatus,
            title: o.suspendedAt
              ? 'Suspended — cannot scan.'
              : entitled
                ? `Entitled: ${o.subscriptionTier} and ACTIVE.`
                : `NOT entitled — tier is ${o.subscriptionTier}. Scans are refused. Tier and status are separate columns; ACTIVE here only means "not lapsed or suspended".`,
          }),
        ]),
        el('td', {}, [
          renewal
            ? el('span', {
                class: tonePillClass(renewal.tone),
                textContent: renewal.text,
                title: renewal.title ?? '',
              })
            : el('span', { class: 'cell-muted', textContent: '—' }),
        ]),
        el('td', { class: 'num', textContent: fmtInt(o.metrics.activeMembers) }),
        el('td', { class: 'num', textContent: fmtInt(o.metrics.activeDevices) }),
        el('td', { class: 'num', textContent: fmtInt(o.metrics.venues) }),
        el('td', { class: 'num', textContent: fmtInt(o.metrics.scans) }),
        el('td', { class: `num ${passbackCls}`, textContent: fmtInt(o.metrics.passbackHits) }),
        el('td', { class: 'num', textContent: fmtInt(o.metrics.rejectedScans) }),
        el('td', { class: 'cell-muted', textContent: relativeTime(o.metrics.lastScanAt) }),
      ],
    );
  });

  container.append(
    el('div', { class: 'table-wrap' }, [
      el('table', { class: 'data-table' }, [thead, el('tbody', {}, rows)]),
    ]),
  );
}

function renderPagination(
  container: HTMLElement,
  data: { page: number; totalPages: number; total: number },
  onPage: (page: number) => void,
): void {
  const prev = el(
    'button',
    {
      class: 'btn btn-ghost',
      disabled: data.page <= 1,
      onClick: () => onPage(data.page - 1),
    },
    ['‹ Prev'],
  );
  const next = el(
    'button',
    {
      class: 'btn btn-ghost',
      disabled: data.page >= data.totalPages,
      onClick: () => onPage(data.page + 1),
    },
    ['Next ›'],
  );
  container.append(
    el('div', { class: 'pagination' }, [
      prev,
      el('span', {
        class: 'cell-muted',
        textContent: `Page ${data.page} of ${Math.max(data.totalPages, 1)} · ${fmtInt(data.total)} teams`,
      }),
      next,
    ]),
  );
}
